import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, RefreshCw } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["new", "contacted", "scheduled", "closed"];
const FILTERS = [
  { key: "", label: "All" },
  { key: "enrollment", label: "Enrollments" },
  { key: "contact", label: "Contact" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, st] = await Promise.all([
        api.get("/admin/submissions", { params: filter ? { source: filter } : {} }),
        api.get("/admin/stats"),
      ]);
      setItems(subs.data);
      setStats(st.data);
    } catch {
      toast.error("Could not load submissions.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setItems((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.patch(`/admin/submissions/${id}`, { status }); } catch { toast.error("Update failed."); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try { await api.delete(`/admin/submissions/${id}`); toast.success("Deleted."); load(); } catch { toast.error("Delete failed."); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-linen">
      <Seo title="Submissions — Let Go Wellness Admin" description="Manage enrollment and contact requests." path="/admin" />
      <header className="sticky top-0 z-20 border-b border-border bg-linen/85 backdrop-blur-lg">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-6">
            <div className="font-serif text-xl text-ink">Let Go <span className="italic text-clay">Wellness</span> · Admin</div>
            <nav className="flex gap-1">
              <Link to="/admin" className="rounded-full bg-ink px-4 py-1.5 font-sans text-xs text-linen">Submissions</Link>
              <Link to="/admin/ai" data-testid="nav-ai-studio" className="rounded-full px-4 py-1.5 font-sans text-xs text-ink-soft transition-colors hover:bg-white">AI Studio</Link>
              <Link to="/admin/subscribers" data-testid="nav-subscribers" className="rounded-full px-4 py-1.5 font-sans text-xs text-ink-soft transition-colors hover:bg-white">Subscribers</Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-sans text-xs text-muted-foreground sm:inline">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 font-sans text-xs text-ink-soft transition-colors hover:bg-white"><LogOut size={13} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl text-ink">Submissions</h1>
            {stats && <p className="mt-2 font-sans text-sm text-muted-foreground" data-testid="admin-stats">{stats.total} total · <span className="text-clay">{stats.new} new</span> · {stats.enrollment} enrollments · {stats.contact} contact</p>}
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 font-sans text-xs text-ink-soft transition-colors hover:bg-linen"><RefreshCw size={13} /> Refresh</button>
        </div>

        <div className="mb-6 flex gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={`admin-filter-${f.label.toLowerCase()}`} onClick={() => setFilter(f.key)}
              className={`rounded-full px-4 py-2 font-sans text-xs transition-colors ${filter === f.key ? "bg-ink text-linen" : "border border-border bg-white text-ink-soft hover:bg-linen"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="font-sans text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <div className="border border-border bg-white p-16 text-center font-sans text-sm text-muted-foreground">No submissions yet.</div>
        ) : (
          <div className="space-y-px overflow-hidden border border-border bg-border" data-testid="admin-submissions">
            {items.map((s) => (
              <div key={s.id} data-testid={`admin-row-${s.id}`} className="bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <h2 className="font-serif text-xl text-ink">{s.name}</h2>
                      <span className={`rounded-full px-2.5 py-0.5 font-sans text-[10px] uppercase tracking-wide ${s.source === "contact" ? "bg-stone-100 text-stone-600" : "bg-amber-50 text-clay"}`}>{s.source}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-sans text-sm text-ink-soft">
                      <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 hover:text-ink"><Mail size={13} />{s.email}</a>
                      {s.phone && <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1.5 hover:text-ink"><Phone size={13} />{s.phone}</a>}
                      <span className="text-muted-foreground">{fmt(s.createdAt)}</span>
                    </div>
                    <p className="mt-3 font-sans text-sm text-ink-soft"><span className="text-muted-foreground">Concern:</span> {s.concern}{s.insurance ? ` · ${s.insurance}` : ""}{s.ageRange ? ` · ${s.ageRange}` : ""}</p>
                    {s.message && <p className="mt-2 max-w-2xl font-sans text-sm italic text-ink-soft">"{s.message}"</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={s.status || "new"} onValueChange={(v) => setStatus(s.id, v)}>
                      <SelectTrigger data-testid={`admin-status-${s.id}`} className="w-36 rounded-none border-border font-sans text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st} className="capitalize">{st}</SelectItem>)}</SelectContent>
                    </Select>
                    <button data-testid={`admin-delete-${s.id}`} onClick={() => remove(s.id)} className="rounded-full border border-border p-2.5 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
